import { useEffect } from 'react';
import { Button, Modal, ModalBody, ModalHeader } from 'reactstrap';
import { useNavigate, useParams } from 'react-router';
import { useQuery } from '@apollo/client';
import Header from '../components/layout/Header';
import DashboardMap from '../components/multi-project-dashboard/DashboardMap';
import DashboardSearch from '../components/multi-project-dashboard/DashboardSearch';
import DashboardRightMenu from '../components/multi-project-dashboard/DashboardRightMenu';
import Content from '../components/layout/Content';
import DashboardViewButtons from '../components/multi-project-dashboard/DashboardViewButtons';
import { useMultiProjectDashboardContext } from '../contexts/MultiProjectDashboardContext';
import { NEW_DASHBOARD_ID } from '../hooks/useDashboardState';
import { OwnedDashboardsQuery } from '../queries';
import { showError } from '../utils/toast';

const MultiProjectDashboardView = () => {
  const { id } = useParams();
  const {
    dashboards: {
      loadAndSelectDashboard,
      dashboardError,
      setDashboardError,
      selectDashboard,
      setOwnedDashboardsData,
    },
    resetState,
  } = useMultiProjectDashboardContext();

  useEffect(() => {
    // Error state should be reset on switching between dashboards
    setDashboardError(null);
    if (id == null) {
      // If the id is null, select the new dashboard
      // without setting or resetting any underlying data
      selectDashboard(NEW_DASHBOARD_ID);
    } else {
      // Otherwise, load the dashboard data for the given id
      // and select the dashboard if successful
      loadAndSelectDashboard(id);
    }
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const { data: ownedDashboardsData, error: ownedDashboardsError } = useQuery(
    OwnedDashboardsQuery,
  );

  useEffect(() => {
    if (ownedDashboardsData?.ownedMultiProjectDashboards) {
      setOwnedDashboardsData(ownedDashboardsData.ownedMultiProjectDashboards);
    }
  }, [ownedDashboardsData]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (ownedDashboardsError) {
      showError(ownedDashboardsError);
    }
  }, [ownedDashboardsError]); // eslint-disable-line react-hooks/exhaustive-deps

  const navigate = useNavigate();

  return (
    <>
      <Header>
        <div className="d-flex">
          <DashboardSearch />
          <DashboardViewButtons />
        </div>
      </Header>
      <Content noPadding className="flex-row">
        <div className="position-relative h-100 w-100">
          <DashboardMap />
        </div>
        <DashboardRightMenu />
      </Content>
      <Modal isOpen={dashboardError != null} toggle={() => {}}>
        <ModalHeader tag="div">Failed to fetch dashboard</ModalHeader>
        <ModalBody>
          <div>
            We were unable to fetch the dashboard. Please select a different
            dashboard.
          </div>
          <div className="d-flex justify-content-end mt-3">
            <Button
              onClick={() => {
                resetState();
                navigate('/app/dashboard');
              }}
              outline
              size="sm"
            >
              Return to dashboard home
            </Button>
          </div>
        </ModalBody>
      </Modal>
    </>
  );
};

export default MultiProjectDashboardView;
